/**
 * Batch 18-G19 — PassportLearningCenter recommendation
 *                display-state helper.
 *
 * Pure function that maps a `useSmartRecommendations` scored item's
 * `whyShowing` + content-quality flags into a small Passport-side
 * display state. Implements the G18 plan
 * (`docs/training/TRAINING_RECOMMENDATION_PASSPORT_LEARNING_CENTER_ADOPTION_PLAN_2026-06-08.md`)
 * Option B — Passport-specific display adapter over
 * `useSmartRecommendations`.
 *
 * Boundaries (mirroring G18 §8 / §9 / §13):
 *
 *   - **No** Supabase, React, React Query, or any hook import.
 *     Helper is synchronous and side-effect-free.
 *   - **No** import of the G4 ranked envelope
 *     (`RankedTrainingRecommendation`,
 *     `TrainingRecommendationCandidate`) — the Passport surface
 *     is not a ticket-bridge consumer.
 *   - **No** import of the G4 ticket-side helpers
 *     (`rankTicketTrainingRecommendations`,
 *     `buildTicketTrainingRecommendationCandidate`).
 *   - **No** import of the F36 evidence hook or the F32 derivation
 *     helper.
 *   - **No** `window.open` / modal mount.
 *   - The only G4-type the helper depends on is the surface union
 *     (`TrainingRecommendationSurface`), and the only token it
 *     emits is the literal `'passport'`.
 *
 * The Passport reason vocabulary is intentionally bounded and
 * Passport-specific. It is NOT the G4 ticket-tier
 * `TrainingRecommendationReasonCode` union — the G18 plan §6
 * explicitly chose a Passport-side union to avoid forcing
 * ticket-tier codes here.
 *
 * Forbidden helper output (asserted by the G19 source-pin):
 *
 *   - `Verified` (standalone) — collides with G16 `Verified by
 *     manager` evidence label. Use the renamed
 *     `OptiBay reviewed content` chip instead.
 *   - `Verified by manager`, `Verification pending`,
 *     `Verification revoked`, `Manual completion submitted`
 *     (G16 evidence labels — belong only on
 *     `LearningRequirementsTab`).
 *   - `Required by shop`, `Manager verification required`,
 *     `Overdue`, `Due soon` (G14 policy labels — belong only on
 *     `LearningRequirementsTab` / `TicketTrainingTab`).
 *   - `Certified`, `Confidence`, `Previously completed`
 *     (never on any surface in this lane).
 */

import type { TrainingRecommendationSurface } from '@/types/trainingRecommendations';

/**
 * Bounded Passport reason vocabulary. Each code maps 1:1 to a
 * stable user-facing `reasonLabel`. Adding a new code requires
 * widening the union here AND the G19 source-pin assertions.
 */
export type PassportRecommendationReasonCode =
  | 'domain_match'
  | 'level_match'
  | 'style_match'
  | 'optibay_reviewed'
  | 'quick_win'
  | 'general_relevance';

export interface PassportRecommendationDisplayBadge {
  label: string;
  testId: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}

export interface PassportRecommendationDisplayState {
  surface: TrainingRecommendationSurface;
  reasonCode: PassportRecommendationReasonCode;
  reasonLabel: string;
  reasonFragment: string | null;
  displayBadges: PassportRecommendationDisplayBadge[];
}

export interface PassportRecommendationDisplayStateInput {
  whyShowing?: string | null;
  isOptibayVerifiedContent?: boolean;
  isLive?: boolean;
  isReferenceOnly?: boolean;
}

const GENERAL_RELEVANCE_FALLBACK: { code: PassportRecommendationReasonCode; label: string } = {
  code: 'general_relevance',
  label: 'Recommended based on general relevance',
};

/**
 * Map a single (already-trimmed) `whyShowing` clause to a reason
 * code + label. Returns `null` if the clause is empty or
 * unrecognised — callers either fall back to the next clause or
 * to `general_relevance`.
 */
function mapClause(
  clause: string,
): { code: PassportRecommendationReasonCode; label: string } | null {
  if (!clause) return null;
  // Order matters: more-specific patterns first.
  if (/^Matches your .+ focus$/i.test(clause)) {
    // Preserve the original "Matches your {domain} focus" copy
    // verbatim so a technician sees the domain they configured.
    return { code: 'domain_match', label: clause };
  }
  if (/Right for your current skill level/i.test(clause)) {
    return { code: 'level_match', label: 'Right for your current skill level' };
  }
  if (/Fits your learning style/i.test(clause)) {
    return { code: 'style_match', label: 'Fits your learning style' };
  }
  if (/OptiBay verified quality/i.test(clause)) {
    // Renamed per G18 §5 to avoid collision with G16
    // `Verified by manager`. The Passport content-quality concept
    // is "we reviewed this artifact", NOT "a manager attested the
    // technician completed it".
    return { code: 'optibay_reviewed', label: 'OptiBay reviewed content' };
  }
  if (/Quick watch/i.test(clause)) {
    return { code: 'quick_win', label: 'Quick watch' };
  }
  if (/Recommended based on general relevance/i.test(clause)) {
    return GENERAL_RELEVANCE_FALLBACK;
  }
  return null;
}

export function buildPassportRecommendationDisplayState(
  input: PassportRecommendationDisplayStateInput,
): PassportRecommendationDisplayState {
  // Split the `useSmartRecommendations` `whyShowing` string by the
  // ` • ` separator the hook uses, and trim each clause.
  const clauses = (input.whyShowing ?? '')
    .split('•')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  let primary: { code: PassportRecommendationReasonCode; label: string } | null = null;
  let secondary: { code: PassportRecommendationReasonCode; label: string } | null = null;

  for (const clause of clauses) {
    const mapped = mapClause(clause);
    if (!mapped) continue;
    if (primary === null) {
      primary = mapped;
      continue;
    }
    // Skip duplicate codes so a clause never becomes its own
    // fragment.
    if (mapped.code === primary.code) continue;
    secondary = mapped;
    break;
  }

  if (primary === null) {
    primary = GENERAL_RELEVANCE_FALLBACK;
  }

  const displayBadges: PassportRecommendationDisplayBadge[] = [];

  if (input.isOptibayVerifiedContent === true) {
    displayBadges.push({
      label: 'OptiBay reviewed content',
      testId: 'passport-recommendation-badge-optibay-reviewed-content',
      variant: 'secondary',
    });
  }
  if (input.isLive === true) {
    displayBadges.push({
      label: 'Live',
      testId: 'passport-recommendation-badge-live',
      variant: 'outline',
    });
  }
  if (input.isReferenceOnly === true) {
    displayBadges.push({
      label: 'Reference only',
      testId: 'passport-recommendation-badge-reference-only',
      variant: 'outline',
    });
  }

  return {
    surface: 'passport',
    reasonCode: primary.code,
    reasonLabel: primary.label,
    reasonFragment: secondary ? secondary.label : null,
    displayBadges,
  };
}
